import { useState, useRef, useCallback } from 'react';
import ReactCrop, { type Crop, type PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Upload, X, Loader2, Crop as CropIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import api from '@/lib/api';

interface ImageUploaderProps {
  value?: string;
  onChange: (url: string) => void;
  label?: string;
  className?: string;
  targetWidth?: number;
  targetHeight?: number;
}

interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Convert a crop expressed in the on-screen preview's pixels into the source
 * image's own pixels, which is what the server extracts against.
 *
 * We send this rect alongside the ORIGINAL file rather than exporting the crop
 * from a <canvas>. A canvas export re-encodes (it was JPEG q0.92), and the
 * server then re-encodes to WebP — so a cropped image used to be compressed
 * twice before anyone ever saw it. Handing over the rect keeps it to a single
 * encode, which is the difference a designer notices on flat colour and
 * gradients.
 *
 * `naturalWidth`/`naturalHeight` already account for EXIF orientation in every
 * current browser, and the server rotates before extracting, so both sides
 * measure the same picture.
 */
function toNaturalRect(image: HTMLImageElement, crop: PixelCrop): CropRect {
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  return {
    x: Math.round(crop.x * scaleX),
    y: Math.round(crop.y * scaleY),
    w: Math.round(crop.width * scaleX),
    h: Math.round(crop.height * scaleY),
  };
}

export default function ImageUploader({
  value,
  onChange,
  label = 'Image',
  className,
  targetWidth,
  targetHeight,
}: ImageUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const uploadId = `upload-${label.toLowerCase().replace(/\s/g, '-')}`;

  const [cropFile, setCropFile] = useState<File | null>(null);
  const [cropSrc, setCropSrc] = useState('');
  const [crop, setCrop] = useState<Crop>();

  const hasCropTarget = !!(targetWidth && targetHeight);
  const aspect = hasCropTarget ? targetWidth / targetHeight : undefined;

  const uploadBlob = useCallback(
    async (blob: Blob, filename: string, cropRect?: CropRect) => {
      setUploading(true);
      setError('');
      try {
        const formData = new FormData();
        formData.append('file', blob, filename);
        const params = new URLSearchParams();
        if (targetWidth) params.set('width', String(targetWidth));
        if (targetHeight) params.set('height', String(targetHeight));
        params.set('fit', 'cover');
        if (cropRect) {
          params.set('crop_x', String(cropRect.x));
          params.set('crop_y', String(cropRect.y));
          params.set('crop_w', String(cropRect.w));
          params.set('crop_h', String(cropRect.h));
        }
        const qs = params.toString();
        const result = await api.upload<{ url: string }>(
          `/admin/upload/cms${qs ? `?${qs}` : ''}`,
          formData,
        );
        onChange(result.url);
      } catch {
        setError('Upload failed. Please try again.');
      } finally {
        setUploading(false);
      }
    },
    [onChange, targetWidth, targetHeight],
  );

  const handleFile = useCallback(
    (file: File) => {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('File must be under 10MB');
        return;
      }
      setError('');

      if (hasCropTarget) {
        setCropFile(file);
        setCropSrc(URL.createObjectURL(file));
        setCrop(undefined);
      } else {
        uploadBlob(file, file.name);
      }
    },
    [hasCropTarget, uploadBlob],
  );

  function onImageLoaded(e: React.SyntheticEvent<HTMLImageElement>) {
    imgRef.current = e.currentTarget;
    const { width, height } = e.currentTarget;
    const cropAspect = aspect!;
    let cropW: number, cropH: number;
    if (width / height > cropAspect) {
      cropH = height * 0.9;
      cropW = cropH * cropAspect;
    } else {
      cropW = width * 0.9;
      cropH = cropW / cropAspect;
    }
    setCrop({
      unit: 'px',
      x: (width - cropW) / 2,
      y: (height - cropH) / 2,
      width: cropW,
      height: cropH,
    });
  }

  async function handleCropConfirm() {
    if (!imgRef.current || !crop || !cropFile) return;
    const pixelCrop: PixelCrop = {
      unit: 'px',
      x: crop.x ?? 0,
      y: crop.y ?? 0,
      width: crop.width ?? 0,
      height: crop.height ?? 0,
    };
    try {
      const rect = toNaturalRect(imgRef.current, pixelCrop);
      const file = cropFile;
      closeCropModal();
      // The original file goes up untouched; the server extracts the rect and
      // encodes once. See toNaturalRect above for why this isn't a canvas blob.
      await uploadBlob(file, file.name, rect);
    } catch {
      setError('Crop failed. Please try again.');
    }
  }

  function closeCropModal() {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropFile(null);
    setCropSrc('');
    setCrop(undefined);
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    if (inputRef.current) inputRef.current.value = '';
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(true);
  }

  function handleDragLeave() {
    setDragOver(false);
  }

  function handleRemove() {
    onChange('');
    setError('');
  }

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={uploadId}>
        {label}
      </label>

      {value ? (
        <div className="relative inline-block">
          <img
            src={value}
            alt="Uploaded preview"
            className="h-32 w-auto rounded-lg border border-gray-200 object-cover"
          />
          <button
            type="button"
            onClick={handleRemove}
            className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow border border-gray-200 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600"
            aria-label="Remove image"
          >
            <X className="h-3.5 w-3.5 text-gray-500" />
          </button>
        </div>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
          role="button"
          tabIndex={0}
          aria-label={`Upload ${label}`}
          className={cn(
            'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 cursor-pointer transition-colors',
            dragOver ? 'border-sage-500 bg-sage-50' : 'border-gray-300 hover:border-sage-400 hover:bg-gray-50',
            uploading && 'pointer-events-none opacity-50'
          )}
        >
          {uploading ? (
            <>
              <Loader2 className="h-8 w-8 text-sage-500 animate-spin" aria-hidden="true" />
              <p className="text-sm text-gray-500" aria-live="polite">Uploading...</p>
            </>
          ) : (
            <>
              <Upload className="h-8 w-8 text-gray-400" aria-hidden="true" />
              <p className="text-sm text-gray-500">
                Drop an image here or <span className="text-sage-600 font-medium">browse</span>
              </p>
              <p className="text-xs text-gray-400">PNG, JPG, WebP up to 10MB</p>
              {hasCropTarget && (
                <p className="text-xs text-gray-400 flex items-center gap-1">
                  <CropIcon className="h-3 w-3" aria-hidden="true" />
                  Recommended: {targetWidth} &times; {targetHeight}
                </p>
              )}
            </>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        id={uploadId}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="sr-only"
        aria-hidden="true"
      />

      {error && (
        <p className="mt-1 text-sm text-red-600" role="alert">{error}</p>
      )}

      {/* Crop Modal */}
      {cropSrc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeCropModal}>
          <div
            className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">
                Crop Image
                {hasCropTarget && (
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    {targetWidth} &times; {targetHeight}
                  </span>
                )}
              </h3>
              <button type="button" onClick={closeCropModal} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-5 flex items-center justify-center bg-gray-50">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                aspect={aspect}
                className="max-h-[60vh]"
              >
                <img
                  src={cropSrc}
                  onLoad={onImageLoaded}
                  alt="Crop preview"
                  style={{ maxHeight: '60vh', maxWidth: '100%' }}
                />
              </ReactCrop>
            </div>

            <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-gray-200">
              <button
                type="button"
                onClick={closeCropModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCropConfirm}
                disabled={uploading}
                className="px-4 py-2 text-sm font-medium text-white bg-sage-600 rounded-lg hover:bg-sage-700 disabled:opacity-50 flex items-center gap-2"
              >
                {uploading && <Loader2 className="h-4 w-4 animate-spin" />}
                Crop &amp; Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
